import { useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { startOfToday, subDays, endOfHour, getUnixTime } from 'date-fns';
import { usePermissions, useSite, useHasFeatureFlag, useModal } from '/src/hooks';
import { useNavigate } from 'react-router-dom';

import { AddSiteModal } from '/src/components/modals/AddSiteModal';
import { SitePickerBar } from '/src/components/site-picker/index';
import { InstallationBanner } from '/src/components/banners/InstallationBanner';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';
import { FEATURES, SPECIAL_DATE_RANGES } from '/src/features/_global/constants';
import { TIMEZONE_OFFSET_SECONDS } from '/src/utils/date';
import { dashboardQuery } from '../queries';
import { SourceSelector } from '/src/components/source-selector/source-selector';
import { FirstVisitModal } from '/src/features/web-analytics/components/modals/FirstVisitModal';

export function HomeDashboardContent({ dateRange, sections }) {
  const navigate = useNavigate();
  const { sites, selectedSite, loadingSites, reloadSites } = useSite();
  const permissions = usePermissions();
  const modal = useModal();
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;
  const hasGaImport = useHasFeatureFlag('ga-import');

  const convertedDateRange = useMemo(() => {
    const start = getUnixTime(subDays(startOfToday(), dateRange)) - TIMEZONE_OFFSET_SECONDS;
    const end = getUnixTime(endOfHour(new Date()));
    let special;
    if (dateRange === 0) {
      special = SPECIAL_DATE_RANGES.TODAY;
    } else if (dateRange === 7) {
      special = SPECIAL_DATE_RANGES.LAST_7_DAYS;
    } else {
      special = SPECIAL_DATE_RANGES.LAST_30_DAYS;
    }

    return {
      start,
      end,
      special,
      previousStart: start - (end - start) - 1,
      previousEnd: start - 1,
    };
  }, [dateRange]);

  const { data, isLoading } = useQuery({
    ...dashboardQuery({
      siteId: selectedSite?.id,
      startAt: convertedDateRange.start,
      endAt: convertedDateRange.end,
      previousStartAt: convertedDateRange.previousStart,
      previousEndAt: convertedDateRange.previousEnd,
    }),
    enabled: Boolean(selectedSite?.id),
  });

  const dashboardData = data?.dashboard;

  useEffect(() => {
    if (isLoading || !selectedSite) {
      return;
    }

    let hasSeenGAModal = false;

    try {
      hasSeenGAModal = localStorage.getItem('hasSeenGAModal') === 'true';
    } catch (error) {
      console.warn('localStorage unavailable:', error);
    }

    if (hasGaImport && !hasSeenGAModal) {
      modal.show(
        <FirstVisitModal
          isGA={true}
          onClose={() => {
            try {
              localStorage.setItem('hasSeenGAModal', 'true');
            } catch (error) {
              console.warn('Failed to save modal state:', error);
            }

            navigate('/web-analytics');
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('openSourcePicker'));
            }, 100);
          }}
        />,
      );
    }
  }, [modal, isLoading, hasGaImport, selectedSite, navigate]);

  if (!loadingSites && !sites.length && canEditSites) {
    return (
      <AddSiteModal
        customTitle="Add a site to get started"
        onExtraSteps={() => {
          reloadSites();
        }}
        onCancel={() => {
          navigate('/options', { replace: true });
        }}
      />
    );
  }

  return (
    <>
      <SitePickerBar
        Picker={SourceSelector}
        sites={sites}
        loading={loadingSites}
        addSiteOption={canEditSites}
        pageDisplayName="Home dashboard"
      />
      <PaymentAlerts />
      <>
        <InstallationBanner />
        <div className="flex flex-col p-10">
          <div className="grid grid-flow-row-dense grid-cols-3 gap-5">
            {sections
              .filter((s) => s.enabled)
              .filter((s) => permissions.can('navigate', s.feature)?.allowed)
              .map(({ Component, name }) => (
                <Component key={name} dateRange={convertedDateRange} data={dashboardData} loading={isLoading} />
              ))}
          </div>
        </div>
      </>
    </>
  );
}
